import { HashtagSourcesApiResponse } from 'api/api.types';
import {
  Box,
  color,
  Flex,
  PrimaryHeading,
  spacing,
  Spinner,
  useColorMode,
} from 'deepstash-ui';
import useAuth from 'providers/hooks/useAuth';
import useLayoutContext from 'providers/hooks/useLayoutContext';
import React, { useEffect } from 'react';
import { Source } from 'types/models';
import { normalizeHashtagSources } from 'utils/normalizers/hashtag';
import pageStrings from 'utils/strings/pageStrings';
import TopicContent from './components/TopicContent';
import TopicContentNSI from './components/TopicContentNSI';

interface TopicPageContentProps {
  hashtagSourcesApiResponse: HashtagSourcesApiResponse;
  isFetching: boolean;
  isFirstLoading: boolean;
  hasNextPage?: boolean;
  data: Source[];
  target: React.RefObject<HTMLDivElement>;
}

const TopicPageContent: React.FC<TopicPageContentProps> = ({
  hashtagSourcesApiResponse,
  data,
  isFetching,
  hasNextPage,
  isFirstLoading,
  target,
}) => {
  const { colorMode } = useColorMode();

  const { isLoggedIn } = useAuth();
  const { remeasureLayoutCallbackRef } = useLayoutContext();

  useEffect(() => {
    remeasureLayoutCallbackRef?.current?.();
  }, [isLoggedIn]);

  const normalizedHashtag = normalizeHashtagSources(hashtagSourcesApiResponse);

  return (
    <>
      {data.length > 0 ? (
        !isLoggedIn ? (
          <TopicContentNSI sources={data} />
        ) : (
          <TopicContent
            sources={data}
            hashtag={normalizedHashtag.hashtag}
            hasNextPage={hasNextPage}
          />
        )
      ) : (
        <PrimaryHeading
          size="h2"
          textSize="s"
          type="bold"
          mx={spacing.XXXL.rem}
          color={color[colorMode].textSecondary}
          marginBottom={spacing.XS.rem}
        >
          {pageStrings.topic.noContentMessage}
        </PrimaryHeading>
      )}

      {!isFirstLoading && !isLoggedIn && isFetching && (
        <Flex justifyContent="center">
          <Spinner color={color[colorMode].textSecondary} />
        </Flex>
      )}

      <Box h="6rem" ref={target} />
    </>
  );
};

export default TopicPageContent;
